import React, { useEffect } from "react";
import styled from "styled-components";
import { ReactComponent as WarningTriangleIcon } from "../../assets/svg/warning-mark.svg";
import { ReactComponent as CloseIconError } from "../../assets/svg/close.svg";
import { ReactComponent as CheckCircleIcon } from "../../assets/svg/Check_circle_consent.svg";
import {
  ERROR_50,
  ERROR_200,
  ERROR_300,
  ERROR_600,
  SUCCESS_50,
  SUCCESS_200,
  SUCCESS_300,
  SUCCESS_600,
  WARNING_50,
  WARNING_200,
  WARNING_300,
  WARNING_600
} from "../../constant/colors";
import { fontWeights, sizes } from "../../constant/styles";

const BannerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  gap: 8px;
  padding: 8px 6px;
  background-color: ${(props) => props.$backgroundColor};
  border-bottom: ${(props) => `1px solid ${props.$borderColor}`};
`;

const IconWrapper = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;

    path {
      ${(props) => props.$iconStyle};
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  flex: 1;
`;

const Text = styled.div`
  font-family: "Open Sans", sans-serif;
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.small};
  line-height: 1.5;
  color: ${(props) => props.$textColor};
`;

const CloseButton = styled.div`
  width: 16.667px;
  height: 16.667px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  svg {
    width: 9.653px;
    height: 10.055px;

    path {
      fill: ${(props) => props.$strokeColor};
      stroke: ${(props) => props.$strokeColor};
    }
  }

  &:hover {
    opacity: 0.8;
  }
`;

const BANNER_CONFIG = {
  success: {
    backgroundColor: SUCCESS_50,
    borderColor: SUCCESS_200,
    textColor: SUCCESS_600,
    iconComponent: CheckCircleIcon,
    iconStyle: `stroke: ${SUCCESS_600}`,
    closeStrokeColor: SUCCESS_300
  },
  warning: {
    backgroundColor: WARNING_50,
    borderColor: WARNING_200,
    textColor: WARNING_600,
    iconComponent: WarningTriangleIcon,
    iconStyle: `fill: ${WARNING_600}`,
    closeStrokeColor: WARNING_300
  },
  error: {
    backgroundColor: ERROR_50,
    borderColor: ERROR_200,
    textColor: ERROR_600,
    iconComponent: WarningTriangleIcon,
    iconStyle: `fill: ${ERROR_600}`,
    closeStrokeColor: ERROR_300
  }
};

/**
 * Reusable banner component for displaying success, warning, or error messages
 * @param {Object} props
 * @param {'success' | 'warning' | 'error'} props.type - The type of banner
 * @param {string} props.message - The message to display
 * @param {Function} props.onClose - Callback when close button is clicked
 */
const Banner = ({ type, message, onClose }) => {
  if (!type || !message) return null;

  const config = BANNER_CONFIG[type];
  if (!config) return null;

  const IconComponent = config.iconComponent;

  // Auto-dismiss success banners after 3 seconds
  useEffect(() => {
    if (type === "success" && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [type, onClose]);

  return (
    <BannerContainer $backgroundColor={config.backgroundColor} $borderColor={config.borderColor}>
      <IconWrapper $iconStyle={config.iconStyle}>
        <IconComponent />
      </IconWrapper>
      <Content>
        <Text $textColor={config.textColor}>{message}</Text>
      </Content>
      <CloseButton $strokeColor={config.closeStrokeColor} onClick={onClose}>
        <CloseIconError />
      </CloseButton>
    </BannerContainer>
  );
};

export default Banner;
